import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { withSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';

import { verifyPassword } from 'plugins/validation/validation-rules';
import { verifyInvitationToken, register } from 'services/user.service';
import layoutNames from 'utils/enums/layout-names.enum';

// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles';

// core components
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Button from 'components/CustomButtons/Button';
import CustomInput from 'components/CustomInput/CustomInput';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardFooter from 'components/Card/CardFooter';
import SnackMessage from 'components/Snackbar/SnackMessage';

import styles from 'assets/jss/material-dashboard-pro-react/views/registerPageStyle';

const localStyles = {
	inputWrapper: {
		width: '100%',
		display: 'inline-flex',
		transition: 'margin-bottom 0.3s ease',
	},
	inputWrapperBig: {
		marginBottom: '20px',
		"@media (max-width: 768px)": {
			marginBottom: '35px',
		},
		"@media (max-width: 450px)": {
			marginBottom: '60px',
		},
	},
	container: {
		position: 'relative',
		zIndex: '3',
		paddingRight: "15px",
		paddingLeft: "15px",
		marginRight: "auto",
		marginLeft: "auto",
		width: '1200px',
		"@media (max-width: 450px)": {
			paddingRight: "5px",
			paddingLeft: "5px",
		},
	},
};

const useStyles = makeStyles(styles);
const useLocalStyles = makeStyles(localStyles);

const passwordErrorMessage = 'Password should be at least 8 characters long and contain lower and upper case letters, at least one number and one character ( ! " # $ % & \' ( ) * +)';

const RegisterPage = ({ enqueueSnackbar, location }) => {

	const classes = { ...useStyles(), ...useLocalStyles() };
	const history = useHistory();
	const { control, watch, trigger, handleSubmit, formState } = useForm();
	const { errors } = formState;

	const [loading, setLoading] = useState(true);
	const [activationToken, setActivationToken] = useState('');
	const [email, setEmail] = useState('');
	const [isUniqUserName, setIsUniqUserName] = useState(true);
	const watchPassword = watch('password');

	const verifyToken = async () => {
		try {
			const token = new URLSearchParams(location.search).get('token');
			const {
				email,
				success,
			} = await verifyInvitationToken(token);

			if (success) {
				setActivationToken(token);
				setEmail(email);
				setLoading(false);
			} else {
				history.replace(`/${layoutNames.auth}`);
			}
		} catch (e) {
			enqueueSnackbar(e.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			});
			history.replace(`/${layoutNames.auth}`);
		}
	};

	useEffect(() => {
		verifyToken();
	}, []);

	useEffect(() => {
		trigger('username');
	}, [isUniqUserName]);

	// eslint-disable-next-line no-unused-vars
	const onSubmit = async ({ repeatPassword, ...data }) => {
		try {
			const trimmedData = Object
				.entries(data)
				.reduce((acc, [key, value]) => {
					acc[key] = value.trim();
					return acc;
				}, {});

			await register({
				...trimmedData,
				activationToken,
			});
			history.replace(`/${layoutNames.auth}`);

			const successMessage = ['You have been registered successfully.', 'Please, login to the system!'];
			enqueueSnackbar(successMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
			});
		} catch (e) {
			if (e.errorCode === 'USER_ALREADY_REGISTERED') {
				setIsUniqUserName(false);
			}
			enqueueSnackbar(e.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			});
		}
	};

	const onError = () => {
		const errorMessage = ['Your registration cannot be completed due to errors in it.', 'Please, check and try again'];
		enqueueSnackbar(errorMessage, {
			content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
		});
	};

	const setWrapperStyle = (fieldName) => {
		const MAX_ERROR_LENGTH = 40;
		if (errors && errors[fieldName] && errors[fieldName].message.length > MAX_ERROR_LENGTH) {
			return `${classes.inputWrapper} ${classes.inputWrapperBig}`;
		}
		return classes.inputWrapper;
	};

	return !loading
		&& (
			<div className={classes.container}>
				<GridContainer justify='center'>
					<GridItem xs={10} sm={8} md={6}>
						<Card>
							<CardHeader
								color='rose'
								className={`${classes.inlineBlock} ${classes.center}`}
							>
								<h4 className={classes.inlineBlock}>Registration</h4>
							</CardHeader>
							<form onSubmit={handleSubmit(onSubmit, onError)} noValidate>
								<input type='hidden' value='prayer' />
								<CardBody>
									<GridContainer justify='center'>
										<GridItem>
											<Controller
												name="username"
												control={control}
												defaultValue={''}
												rules={{
													required: 'Field should not be empty',
													minLength: {
														value: 5,
														message: 'Username should be at least 5 characters long',
													},
													maxLength: {
														value: 50,
														message: 'Username max length is 50 characters',
													},
													validate: () => {
														return isUniqUserName || 'User with such Username is registered already. Please, enter another Username';
													},
												}}
												render={({ onChange, value }) => (
													<CustomInput
														labelText={'Username *'}
														error={!!errors.username}
														helperText={errors.username ? errors.username.message : ""}
														formControlProps={{
															fullWidth: true,
															className: classes.customFormControlClasses
														}}
														inputProps={{
															autoComplete: 'user-name',
															value: value,
														}}
														onChange={(event) => {
															const newValue = event.target.value?.trimStart();
															setIsUniqUserName(true);
															onChange(newValue);
															trigger('username');
														}}
														onBlur={() => {
															trigger('username');
														}}
													/>
												)}
											/>
											<Controller
												name="firstName"
												control={control}
												defaultValue={''}
												rules={{
													required: 'Field should not be empty',
													maxLength: {
														value: 255,
														message: 'First Name max length is 255 characters',
													},
												}}
												render={({ onChange, value }) => (
													<CustomInput
														labelText={'First Name *'}
														error={!!errors.firstName}
														helperText={errors.firstName ? errors.firstName.message : ""}
														formControlProps={{
															fullWidth: true,
															className: classes.customFormControlClasses
														}}
														inputProps={{
															autoComplete: 'first-name',
															value: value,
														}}
														onChange={(event) => {
															const newValue = event.target.value?.trimStart();
															onChange(newValue);
															trigger('firstName');
														}}
														onBlur={() => {
															trigger('firstName');
														}}
													/>
												)}
											/>
											<Controller
												name="lastName"
												control={control}
												defaultValue={''}
												rules={{
													required: 'Field should not be empty',
													maxLength: {
														value: 255,
														message: 'Last Name max length is 255 characters',
													},
												}}
												render={({ onChange, value }) => (
													<CustomInput
														labelText={'Last Name *'}
														error={!!errors.lastName}
														helperText={errors.lastName ? errors.lastName.message : ""}
														formControlProps={{
															fullWidth: true,
															className: classes.customFormControlClasses
														}}
														inputProps={{
															autoComplete: 'last-name',
															value: value,
														}}
														onChange={(event) => {
															const newValue = event.target.value?.trimStart();
															onChange(newValue);
															trigger('lastName');
														}}
														onBlur={() => {
															trigger('lastName');
														}}
													/>
												)}
											/>
											<Controller
												name="email"
												control={control}
												defaultValue={email}
												rules={{
													required: 'Field should not be empty',
												}}
												render={({ onChange, value }) => (
													<CustomInput
														labelText={'Email *'}
														error={!!errors.email}
														helperText={errors.email ? errors.email.message : ""}
														formControlProps={{
															fullWidth: true,
															className: classes.customFormControlClasses
														}}
														inputProps={{
															autoComplete: 'new-email',
															value: value,
															disabled: true
														}}
														onChange={(event) => {
															const newValue = event.target.value?.trimStart();
															onChange(newValue);
															trigger('email');
														}}
														onBlur={() => {
															trigger('email');
														}}
													/>
												)}
											/>
											<div className={setWrapperStyle('password')}>
												<Controller
													name="password"
													control={control}
													defaultValue={''}
													rules={{
														required: 'Field should not be empty',
														maxLength: {
															value: 255,
															message: 'Password max length is 255 characters',
														},
														validate: (value) => {
															return verifyPassword(value) || passwordErrorMessage;
														},
													}}
													render={({ onChange, value }) => (
														<CustomInput
															labelText={'Password *'}
															error={!!errors.password}
															helperText={errors.password ? errors.password.message : ""}
															formControlProps={{
																fullWidth: true,
																className: classes.customFormControlClasses
															}}
															inputProps={{
																type: 'password',
																autoComplete: 'new-password',
																value: value,
															}}
															onChange={(event) => {
																const newValue = event.target.value?.trimStart();
																onChange(newValue);
																trigger('password');
															}}
															onBlur={() => {
																trigger('password');
																trigger('repeatPassword');
															}}
														/>
													)}
												/>
											</div>
											<div className={setWrapperStyle('repeatPassword')}>
												<Controller
													name="repeatPassword"
													control={control}
													defaultValue={''}
													rules={{
														required: 'Field should not be empty',
														validate: (value) => {
															return value === watchPassword || 'Please, make sure your password match';
														},
													}}
													render={({ onChange, value }) => (
														<CustomInput
															labelText={'Repeat Password *'}
															error={!!errors.repeatPassword}
															helperText={errors.repeatPassword ? errors.repeatPassword.message : ""}
															formControlProps={{
																fullWidth: true,
																className: classes.customFormControlClasses
															}}
															inputProps={{
																type: 'password',
																autoComplete: 'new-repeatPassword',
																value: value,
															}}
															onChange={(event) => {
																const newValue = event.target.value?.trimStart();
																onChange(newValue);
																trigger('repeatPassword');
															}}
															onBlur={() => {
																trigger('repeatPassword');
															}}
														/>
													)}
												/>
											</div>
										</GridItem>
									</GridContainer>
								</CardBody>
								<CardFooter className={classes.justifyContentCenter}>
									<Button color='rose' simple size='lg' block type='submit'>
										Continue
                </Button>
								</CardFooter>
							</form>
						</Card>
					</GridItem>
				</GridContainer>
			</div>
		);
}

export default withSnackbar(RegisterPage);
